import InputColors from '@/atoms/inputs/colors/Input';
import useInput from '@/atoms/inputs/hooks/useInput';
import flatInputSizeProps from '@/atoms/inputs/utils/flatInputSizeProps';
import outlineInputSizeProps from '@/atoms/inputs/utils/outlineInputSizeProps';
import Size from '@enums/Size';
import Variant from '@enums/Variant';
import OutlinedInput from '@mui/material/OutlinedInput';
import { styled } from '@mui/material/styles';
import excludeProps from '@/lib/theme/excludeProps';

const StyledOutlinedInput = styled(OutlinedInput, {
  shouldForwardProp: excludeProps('color', 'textProps', 'variant', 'width'),
})(({ color, textProps, variant, width }) => ({
  '& .MuiOutlinedInput-input': {
    ...textProps,
    color: InputColors[color].normal.color,
    backgroundColor: 'transparent',
    padding: variant === Variant.TEXT ? 0 : '4px',
    width: width ?? 'inherit',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
    boxShadow:
      variant === Variant.OUTLINE
        ? `inset 0 0 0 1px ${InputColors[color].normal.borderColor}`
        : 'none',
    transition: 'box-shadow 200ms',
  },
  '&:hover': {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      boxShadow:
        variant === Variant.OUTLINE
          ? `inset 0 0 0 2px ${InputColors[color].hover.borderColor}`
          : 'none',
    },
  },
  '&.Mui-focused': {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      boxShadow:
        variant === Variant.OUTLINE
          ? `0 0 0 2px ${InputColors[color].focus.borderColor}`
          : 'none',
    },
  },
  // Hide number arrows from input fields.
  '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
}));

const TextInput = ({
  autoFocus = false,
  color = 'primary',
  disabled = false,
  debounce = 200,
  endAdornment,
  fullWidth,
  initialValue = '',
  slotProps,
  textAlign,
  maxLength,
  multiline = false,
  name,
  onBlur,
  onFocus,
  onChange,
  onKeyDown,
  placeholder,
  rows,
  size = Size.MEDIUM,
  variant = 'outline',
  width,
}) => {
  const { inputBlur, inputChange, inputFocus, value, inputKeyDown } = useInput({
    debounce,
    maxLength,
    multiline,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    initialValue,
  });

  const textProps =
    variant === 'outline'
      ? { ...outlineInputSizeProps(size) }
      : { ...flatInputSizeProps(size) };

  textProps.textAlign = textAlign;

  return (
    <StyledOutlinedInput
      autoComplete="off"
      autoFocus={autoFocus}
      color={color}
      disabled={disabled}
      endAdornment={endAdornment}
      fullWidth={fullWidth}
      textProps={textProps}
      id={`outlined-input-${name}`}
      multiline={multiline}
      rows={rows}
      slotProps={slotProps}
      textAlign={textAlign}
      name={name}
      onBlur={inputBlur}
      onChange={inputChange}
      onFocus={inputFocus}
      onKeyDown={inputKeyDown}
      placeholder={placeholder}
      value={value || ''}
      variant={variant}
      width={width}
    />
  );
};

export default TextInput;
